import { baseApi } from '@shared/api';

/** Профиль текущего пользователя — ответ GET /api/accounts/me/. */
export interface User {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  is_moderator: boolean;
  /** Плоский список выданных capability (`app_label.codename`). */
  permissions: string[];
}

/** GET /api/accounts/session/ — только факт авторизации и CSRF, без профиля. */
export interface SessionResponse {
  is_authenticated: boolean;
  csrf_token: string;
}

/** POST /login|/register — бэкенд возвращает лишь свежий CSRF-токен. */
export interface AuthResponse {
  csrf_token: string;
}

export interface LoginRequest {
  email: string;
  secret: string;
}

export interface RegisterRequest {
  email: string;
  secret: string;
}

export const sessionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /api/accounts/session
     *
     * Первый запрос при старте приложения:
     * 1. Устанавливает CSRF токен в cookie
     * 2. Возвращает информацию о текущей сессии
     */
    getSession: builder.query<SessionResponse, void>({
      query: () => ({
        url: '/api/accounts/session',
        method: 'GET',
      }),
      providesTags: ['Session'],
    }),

    /**
     * GET /api/accounts/me/
     *
     * Профиль и выданные capability текущего пользователя. Отдаёт 403 анониму,
     * поэтому запрашивается только при is_authenticated. Инвалидируется при
     * login/logout — список прав перечитывается.
     */
    getMe: builder.query<User, void>({
      query: () => ({
        url: '/api/accounts/me/',
        method: 'GET',
      }),
      providesTags: ['Me'],
    }),

    /**
     * POST /api/accounts/login/
     * Требует CSRF токен (добавляется автоматически через prepareHeaders)
     */
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => {
        const formData = new FormData();
        formData.append('email', credentials.email);
        formData.append('secret', credentials.secret);

        return {
          url: '/api/accounts/login/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Session', 'Me'],
    }),

    /**
     * POST /api/accounts/register/
     * Требует CSRF токен (добавляется автоматически через prepareHeaders)
     */
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (data) => {
        const formData = new FormData();
        formData.append('email', data.email);
        formData.append('secret', data.secret);

        return {
          url: '/api/accounts/register/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Session'],
    }),

    /**
     * POST /api/accounts/logout/
     * Требует CSRF токен (добавляется автоматически через prepareHeaders)
     */
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/api/accounts/logout/',
        method: 'POST',
      }),
      invalidatesTags: ['Session', 'Me'],
    }),
  }),
});

export const {
  useGetSessionQuery,
  useGetMeQuery,
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
} = sessionApi;
